import React, { useState, useMemo } from "react";
import { Bar, Pie, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Debounce plugin to throttle hover events
const debouncePlugin = {
  id: "debounceTooltip",
  beforeEvent(chart, args) {
    const event = args.event;
    if (event.type === "mousemove") {
      clearTimeout(chart.debounceTimeout);
      chart.debounceTimeout = setTimeout(() => {
        chart.tooltip.handleEvent(event);
      }, 50); // 50ms debounce
    }
  },
};

ChartJS.register(debouncePlugin);

// Utility: Debounce function
function debounce(func, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Enhanced animation configurations
const chartAnimations = {
  // Bar chart animations
  bar: {
    animation: {
      duration: 800,
      easing: "easeOutQuart",
    },
    transitions: {
      active: {
        duration: 300,
        easing: "easeOutBack",
      },
    },
  },
  // Line chart animations
  line: {
    animation: {
      duration: 1000,
      easing: "easeOutQuart",
    },
  },
  // Pie/Doughnut chart animations
  doughnut: {
    animation: {
      duration: 1200,
      easing: "easeOutQuart",
    },
    rotation: -90,
    circumference: 360,
    cutout: "60%",
  },
};

// Memoize the BarChart component
export const BarChart = React.memo(
  ({
    chartType = "bar",
    chartData,
    options: propOptions,
    onChartHover,
    onChartClick,
    height = "300px",
    animationConfig = {},
  }) => {
    const [hoveredElement, setHoveredElement] = useState(null);

    // Debounced hover handler
    const handleHover = useMemo(
      () =>
        debounce((event, elements) => {
          setHoveredElement(elements[0] || null);
          if (onChartHover && elements.length > 0) {
            onChartHover(event, elements, chartType);
          }
        }, 50),
      [onChartHover, chartType]
    );

    const handleLeave = () => {
      setHoveredElement(null);
    };

    // Common tooltip configuration
    const baseTooltipOptions = {
      enabled: true,
      mode: "index",
      intersect: false,
      animation: {
        duration: 200,
        easing: "easeOutQuart",
      },
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      titleColor: "#fff",
      bodyColor: "#fff",
      padding: 12,
      cornerRadius: 6,
      displayColors: true,
      boxPadding: 6,
    };

    // Enhanced Doughnut chart options
    const doughnutOptions = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        onHover: handleHover,
        onClick: onChartClick,
        cutout: chartAnimations.doughnut.cutout,
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 12,
              padding: 15,
              usePointStyle: true,
              font: {
                size: 11,
                family: "'Inter', sans-serif",
              },
              color: "#2c3e50",
            },
          },
          tooltip: {
            ...baseTooltipOptions,
            callbacks: {
              label: (context) => {
                const label = context.label || "";
                const value = context.parsed;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = Math.round((value / total) * 100);
                return `${label}: ${value} (${percentage}%)`;
              },
            },
          },
        },
        animation: {
          animateScale: true,
          animateRotate: true,
          duration: chartAnimations.doughnut.animation.duration,
          easing: chartAnimations.doughnut.animation.easing,
        },
        elements: {
          arc: {
            borderWidth: 2,
            borderColor: "#fff",
            hoverBorderWidth: 3,
            hoverBorderColor: "#fff",
            hoverOffset: 8,
          },
        },
      }),
      [handleHover, onChartClick]
    );

    // Royalty Fee Receivable Aging Chart (Stacked Bar Chart)
    const royaltyAgingOptions = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        onHover: handleHover,
        onClick: onChartClick,
        plugins: {
          legend: {
            position: "top",
            labels: {
              boxWidth: 12,
              padding: 15,
              usePointStyle: true,
              font: {
                size: 11,
                family: "'Inter', sans-serif",
              },
            },
          },
          tooltip: {
            ...baseTooltipOptions,
            callbacks: {
              label: (context) =>
                `${context.dataset.label}: ${context.parsed.y}`,
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            max: 0.6,
            ticks: {
              stepSize: 0.1,
              callback: (value) => value,
            },
            grid: { color: "rgba(0, 0, 0, 0.1)" },
          },
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        animation: {
          duration: 800,
          easing: "easeOutQuart",
        },
      }),
      [handleHover, onChartClick]
    );

    // Help Desk Tickets Pie Chart
    const helpDeskPieOptions = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        onHover: handleHover,
        onClick: onChartClick,
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              boxWidth: 12,
              padding: 15,
              usePointStyle: true,
              font: {
                size: 11,
                family: "'Inter', sans-serif",
              },
            },
          },
          tooltip: {
            ...baseTooltipOptions,
            callbacks: {
              label: (context) => {
                const label = context.label || "";
                const value = context.parsed;
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const percentage = Math.round((value / total) * 100);
                return `${label}: ${value} (${percentage}%)`;
              },
            },
          },
        },
        animation: {
          duration: 800,
          easing: "easeOutQuart",
        },
      }),
      [handleHover, onChartClick]
    );

    // Month-wise Revenue vs Expenses Line Chart
    const monthlyLineOptions = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        onHover: handleHover,
        onClick: onChartClick,
        plugins: {
          legend: { display: false },
          tooltip: {
            ...baseTooltipOptions,
            callbacks: {
              label: (context) => `Net: ${context.parsed.y}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: "Net (Revenue - Expenses) (M)",
            },
            grid: { color: "rgba(0, 0, 0, 0.1)" },
            ticks: {
              callback: (value) => value,
            },
          },
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        animation: {
          duration: 1000,
          easing: "easeOutQuart",
        },
      }),
      [handleHover, onChartClick]
    );

    // Default bar chart options
    const defaultOptions = useMemo(
      () => ({
        ...propOptions,
        responsive: true,
        maintainAspectRatio: false,
        onHover: handleHover,
        onClick: onChartClick,
        animation: {
          duration: 800,
          easing: "easeOutQuart",
        },
        plugins: {
          ...propOptions?.plugins,
          tooltip: {
            ...baseTooltipOptions,
            ...propOptions?.plugins?.tooltip,
          },
        },
      }),
      [propOptions, handleHover, onChartClick]
    );

    // Select chart type
    const ChartComponent =
      chartType === "monthlyLine"
        ? Line
        : chartType === "helpDeskPie"
        ? Pie
        : chartType === "doughnut"
        ? Doughnut
        : chartType === "royaltyAging"
        ? Bar
        : Bar;

    // Select options based on chart type
    const chartOptions =
      chartType === "royaltyAging"
        ? royaltyAgingOptions
        : chartType === "helpDeskPie"
        ? helpDeskPieOptions
        : chartType === "doughnut"
        ? doughnutOptions
        : chartType === "monthlyLine"
        ? monthlyLineOptions
        : defaultOptions;

    return (
      <div style={{ height, position: "relative" }}>
        <ChartComponent
          data={chartData}
          options={chartOptions}
          onMouseLeave={handleLeave}
        />
      </div>
    );
  }
);
